import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaTimes,
    FaEnvelope,
    FaLock,
    FaUser,
    FaArrowRight,
    FaShieldAlt,
    FaCheckCircle,
} from "react-icons/fa";

export default function LoginModal({ isOpen, onClose, setUser }) {
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setErrors({
            ...errors,
            [e.target.name]: "",
            general: "",
        });

        setSuccess("");
    };

    const validate = () => {
        let newErrors = {};

        if (mode === "register" && !form.name.trim()) {
            newErrors.name = "Full name is required";
        }

        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        }

        if (mode !== "forgot" && !form.password.trim()) {
            newErrors.password = "Password is required";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({});
        setSuccess("");

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            // ============================================
            // FORGOT PASSWORD
            // ============================================
            if (mode === "forgot") {
                await axios.post(
                    "https://dubaihotelrates.com/api/forgot-password",
                    {
                        email: form.email,
                    }
                );

                setSuccess(
                    "If an account exists with this email, a password reset link has been sent."
                );

                setLoading(false);
                return;
            }

            // ============================================
            // REGISTER
            // ============================================
            if (mode === "register") {
                await axios.post(
                    "https://dubaihotelrates.com/api/register",
                    {
                        name: form.name,
                        email: form.email,
                        password: form.password,
                        password_confirmation: form.password,
                    }
                );

                alert("User registered successfully");

                setForm({
                    name: "",
                    email: "",
                    password: "",
                });

                setMode("login");
                setLoading(false);

                return;
            }

            // ============================================
            // LOGIN
            // ============================================
            if (mode === "login") {
                const res = await axios.post(
                    "https://dubaihotelrates.com/api/login",
                    {
                        email: form.email,
                        password: form.password,
                    }
                );

                const userData = res.data.user;

                // Save user
                localStorage.setItem(
                    "user",
                    JSON.stringify(userData)
                );

                setUser(userData);

                alert("Login successful");

                setLoading(false);

                // Close modal
                onClose();

                // Redirect to profile
                navigate("/profile");
            }
        } catch (err) {
            console.error(err);

            setLoading(false);

            // Forgot password error
            if (mode === "forgot") {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Unable to send the reset link. Please try again later.",
                });
            } else {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Authentication failed. Please check your details.",
                });
            }
        }
    };

    // ============================================
    // SWITCH TO LOGIN
    // ============================================
    const goToLogin = () => {
        setMode("login");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    // ============================================
    // SWITCH TO REGISTER
    // ============================================
    const goToRegister = () => {
        setMode("register");
        setErrors({});
        setSuccess("");
    };

    // ============================================
    // SWITCH TO FORGOT PASSWORD
    // ============================================
    const goToForgotPassword = () => {
        setMode("forgot");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    return (
        <div
            className="
                fixed inset-0 z-50
                flex items-center justify-center
                bg-[#0F172A]/60
                backdrop-blur-sm
                px-4
                py-6
            "
            onClick={onClose}
        >
            {/* Modal */}
            <div
                className="
                    relative
                    w-full max-w-md
                    overflow-hidden
                    rounded-[28px]
                    border border-slate-200
                    bg-white
                    shadow-[0_25px_80px_rgba(15,23,42,0.20)]
                "
                onClick={(e) => e.stopPropagation()}
            >
                {/* ============================================
                    TOP ACCENT
                ============================================ */}
                <div className="h-1.5 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

                {/* ============================================
                    DECORATIVE BACKGROUND
                ============================================ */}
                <div className="pointer-events-none absolute -right-20 -top-20 h-44 w-44 rounded-full bg-[#174EA6]/8 blur-3xl" />

                <div className="pointer-events-none absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-[#FFC928]/10 blur-3xl" />

                {/* ============================================
                    CLOSE BUTTON
                ============================================ */}
                <button
                    onClick={onClose}
                    type="button"
                    aria-label="Close"
                    className="
                        absolute
                        right-4
                        top-4
                        z-20
                        flex
                        h-9
                        w-9
                        items-center
                        justify-center
                        rounded-xl
                        bg-slate-100
                        text-slate-500
                        transition-all
                        duration-200
                        hover:bg-[#EAF3FF]
                        hover:text-[#174EA6]
                        hover:scale-105
                    "
                >
                    <FaTimes className="text-sm" />
                </button>

                <div className="relative z-10 p-6 sm:p-8">
                    {/* ============================================
                        HEADER
                    ============================================ */}
                    <div className="text-center">
                        {/* Icon */}
                        <div
                            className="
                                mx-auto
                                flex
                                h-14
                                w-14
                                items-center
                                justify-center
                                rounded-2xl
                                bg-[#EAF3FF]
                                text-[#174EA6]
                                shadow-sm
                            "
                        >
                            {mode === "register" ? (
                                <FaUser className="text-xl" />
                            ) : mode === "forgot" ? (
                                <FaEnvelope className="text-xl" />
                            ) : (
                                <FaLock className="text-xl" />
                            )}
                        </div>

                        {/* Small Label */}
                        <div
                            className="
                                mx-auto
                                mt-5
                                inline-flex
                                items-center
                                gap-2
                                rounded-full
                                bg-[#EAF3FF]
                                px-3
                                py-1.5
                                text-[10px]
                                font-extrabold
                                uppercase
                                tracking-[0.15em]
                                text-[#174EA6]
                            "
                        >
                            <FaShieldAlt className="text-[9px]" />

                            {mode === "register"
                                ? "Join Us"
                                : mode === "forgot"
                                    ? "Account Recovery"
                                    : "Welcome Back"}
                        </div>

                        {/* Title */}
                        <h2
                            className="
                                mt-3
                                text-2xl
                                font-extrabold
                                text-[#0F172A]
                                sm:text-3xl
                            "
                        >
                            {mode === "register"
                                ? "Create Account"
                                : mode === "forgot"
                                    ? "Forgot Password?"
                                    : "Sign In"}
                        </h2>

                        {/* Subtitle */}
                        <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                            {mode === "register"
                                ? "Create your account to manage your hotel bookings."
                                : mode === "forgot"
                                    ? "Enter your email and we'll send you a reset link."
                                    : "Login to manage your hotel bookings and reservations."}
                        </p>
                    </div>

                    {/* ============================================
                        GENERAL ERROR
                    ============================================ */}
                    {errors.general && (
                        <div
                            className="
                                mt-6
                                rounded-2xl
                                border
                                border-red-200
                                bg-red-50
                                px-4
                                py-3
                                text-sm
                                leading-5
                                text-red-600
                            "
                        >
                            {errors.general}
                        </div>
                    )}

                    {/* ============================================
                        SUCCESS MESSAGE
                    ============================================ */}
                    {success && (
                        <div
                            className="
                                mt-6
                                flex
                                items-start
                                gap-3
                                rounded-2xl
                                border
                                border-green-200
                                bg-green-50
                                px-4
                                py-3
                                text-sm
                                leading-5
                                text-green-700
                            "
                        >
                            <FaCheckCircle className="mt-0.5 shrink-0" />

                            <span>{success}</span>
                        </div>
                    )}

                    {/* ============================================
                        FORM
                    ============================================ */}
                    <form
                        onSubmit={handleSubmit}
                        className="mt-6 space-y-4"
                    >
                        {/* ========================================
                            NAME
                        ======================================== */}
                        {mode === "register" && (
                            <div>
                                <label
                                    htmlFor="name"
                                    className="
                                        mb-1.5
                                        block
                                        text-xs
                                        font-bold
                                        text-[#0F172A]
                                    "
                                >
                                    Full Name
                                </label>

                                <div className="relative">
                                    <FaUser
                                        className="
                                            absolute
                                            left-4
                                            top-1/2
                                            -translate-y-1/2
                                            text-xs
                                            text-slate-400
                                        "
                                    />

                                    <input
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        placeholder="Enter your full name"
                                        onChange={handleChange}
                                        className="
                                            w-full
                                            rounded-xl
                                            border
                                            border-slate-200
                                            bg-slate-50
                                            py-3
                                            pl-10
                                            pr-4
                                            text-sm
                                            text-[#0F172A]
                                            outline-none
                                            transition-all
                                            duration-200
                                            placeholder:text-slate-400
                                            focus:border-[#174EA6]
                                            focus:bg-white
                                            focus:ring-4
                                            focus:ring-[#174EA6]/10
                                        "
                                    />
                                </div>

                                {errors.name && (
                                    <p className="mt-1.5 text-xs text-red-500">
                                        {errors.name}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* ========================================
                            EMAIL
                        ======================================== */}
                        <div>
                            <label
                                htmlFor="email"
                                className="
                                    mb-1.5
                                    block
                                    text-xs
                                    font-bold
                                    text-[#0F172A]
                                "
                            >
                                Email Address
                            </label>

                            <div className="relative">
                                <FaEnvelope
                                    className="
                                        absolute
                                        left-4
                                        top-1/2
                                        -translate-y-1/2
                                        text-xs
                                        text-slate-400
                                    "
                                />

                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    placeholder="Enter your email"
                                    onChange={handleChange}
                                    className="
                                        w-full
                                        rounded-xl
                                        border
                                        border-slate-200
                                        bg-slate-50
                                        py-3
                                        pl-10
                                        pr-4
                                        text-sm
                                        text-[#0F172A]
                                        outline-none
                                        transition-all
                                        duration-200
                                        placeholder:text-slate-400
                                        focus:border-[#174EA6]
                                        focus:bg-white
                                        focus:ring-4
                                        focus:ring-[#174EA6]/10
                                    "
                                />
                            </div>

                            {errors.email && (
                                <p className="mt-1.5 text-xs text-red-500">
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        {/* ========================================
                            PASSWORD
                        ======================================== */}
                        {mode !== "forgot" && (
                            <div>
                                <label
                                    htmlFor="password"
                                    className="
                                        mb-1.5
                                        block
                                        text-xs
                                        font-bold
                                        text-[#0F172A]
                                    "
                                >
                                    Password
                                </label>

                                <div className="relative">
                                    <FaLock
                                        className="
                                            absolute
                                            left-4
                                            top-1/2
                                            -translate-y-1/2
                                            text-xs
                                            text-slate-400
                                        "
                                    />

                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={form.password}
                                        placeholder="Enter your password"
                                        onChange={handleChange}
                                        className="
                                            w-full
                                            rounded-xl
                                            border
                                            border-slate-200
                                            bg-slate-50
                                            py-3
                                            pl-10
                                            pr-4
                                            text-sm
                                            text-[#0F172A]
                                            outline-none
                                            transition-all
                                            duration-200
                                            placeholder:text-slate-400
                                            focus:border-[#174EA6]
                                            focus:bg-white
                                            focus:ring-4
                                            focus:ring-[#174EA6]/10
                                        "
                                    />
                                </div>

                                {errors.password && (
                                    <p className="mt-1.5 text-xs text-red-500">
                                        {errors.password}
                                    </p>
                                )}

                                {/* Forgot Password */}
                                {mode === "login" && (
                                    <div className="mt-2 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={goToForgotPassword}
                                            className="
                                                text-xs
                                                font-bold
                                                text-[#174EA6]
                                                transition
                                                hover:text-[#0F172A]
                                            "
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ========================================
                            SUBMIT BUTTON
                        ======================================== */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="
                                group
                                flex
                                w-full
                                items-center
                                justify-center
                                gap-3
                                rounded-xl
                                bg-gradient-to-r
                                from-[#174EA6]
                                to-[#FFC928]
                                px-5
                                py-3.5
                                text-sm
                                font-extrabold
                                text-white
                                shadow-[0_10px_25px_rgba(23,78,166,0.18)]
                                transition-all
                                duration-300
                                hover:-translate-y-0.5
                                hover:shadow-[0_15px_30px_rgba(23,78,166,0.25)]
                                disabled:cursor-not-allowed
                                disabled:opacity-60
                            "
                        >
                            {loading
                                ? "Please wait..."
                                : mode === "register"
                                    ? "Create Account"
                                    : mode === "forgot"
                                        ? "Send Reset Link"
                                        : "Sign In"}

                            {!loading && (
                                <FaArrowRight
                                    className="
                                        text-xs
                                        transition-transform
                                        duration-300
                                        group-hover:translate-x-1
                                    "
                                />
                            )}
                        </button>
                    </form>

                    {/* ============================================
                        BOTTOM NAVIGATION
                    ============================================ */}
                    <div className="mt-6 text-center text-sm text-slate-500">
                        {mode === "login" && (
                            <>
                                Don't have an account?{" "}
                                <button
                                    type="button"
                                    onClick={goToRegister}
                                    className="
                                        font-bold
                                        text-[#174EA6]
                                        transition
                                        hover:text-[#0F172A]
                                    "
                                >
                                    Create account
                                </button>
                            </>
                        )}

                        {mode === "register" && (
                            <>
                                Already have an account?{" "}
                                <button
                                    type="button"
                                    onClick={goToLogin}
                                    className="
                                        font-bold
                                        text-[#174EA6]
                                        transition
                                        hover:text-[#0F172A]
                                    "
                                >
                                    Sign in
                                </button>
                            </>
                        )}

                        {mode === "forgot" && (
                            <button
                                type="button"
                                onClick={goToLogin}
                                className="
                                    inline-flex
                                    items-center
                                    gap-1.5
                                    font-bold
                                    text-[#174EA6]
                                    transition
                                    hover:text-[#0F172A]
                                "
                            >
                                ← Back to login
                            </button>
                        )}
                    </div>

                    {/* ============================================
                        SECURITY NOTE
                    ============================================ */}
                    <div
                        className="
                            mt-6
                            flex
                            items-center
                            justify-center
                            gap-2
                            border-t
                            border-slate-100
                            pt-5
                            text-[10px]
                            font-semibold
                            uppercase
                            tracking-[0.12em]
                            text-slate-400
                        "
                    >
                        <FaShieldAlt className="text-[#174EA6]" />
                        Secure & Protected
                    </div>
                </div>
            </div>
        </div>
    );
}